<script setup lang="ts">
import {computed, nextTick, ref, watch} from "vue";
import {useTextareaAutosize} from "@vueuse/core";
import {Check, FileDown, LoaderCircle, Sparkles} from "@lucide/vue";
import {Textarea} from "@/core/components/ui/textarea";
import {Button} from "@/core/components/ui/button";
import {CollectionClosingAnswers, CollectionClosingView} from "@/medication-checkup/interfaces/collection-closing";
import {Label} from "@/core/components/ui/label";
import {CARD_SHEET} from "@/medication-checkup/services/SceneClasses";
import MedicationCheckupAiBadge from "@/medication-checkup/components/MedicationCheckupAiBadge.vue";
import MedicationCheckupBillingReminder from "@/medication-checkup/components/MedicationCheckupBillingReminder.vue";
import MedicationCheckupKnowledgeAppraisal from "@/medication-checkup/components/MedicationCheckupKnowledgeAppraisal.vue";
import {KnowledgeAppraisalAnswers} from "@/medication-checkup/interfaces/knowledge-appraisal";

/**
 * The card closing the collection interview: the success header, the two
 * free text fields — what the patient asked, the conclusion —, the appraisal
 * of what the patient knows, the billing reminder and the recap to
 * download, shown from the start but offered only once frozen. The words of the
 * copilote are served; how the texts are asked (labels, hints) is the
 * card's own.
 *
 * Typing is local to the scene: the card shows the texts it is handed while
 * live, the ones the server froze once the block is completed. Downloading
 * is the scene's too: the card asks for it and shows the wait — as is the
 * drafting of the conclusion, which the card only tells.
 */

const props = defineProps<{
    view: CollectionClosingView;
    frozen?: boolean;
    /**
     * The texts typed so far, while the closing is live.
     */
    answers?: CollectionClosingAnswers;
    /**
     * The appraisal as it stands, while the closing is live.
     */
    appraisal?: KnowledgeAppraisalAnswers;
    /**
     * Whether the AI is drafting the conclusion.
     */
    drafting?: boolean;
    /**
     * Whether the recap is being fetched.
     */
    downloading?: boolean;
}>();

const emit = defineEmits<{
    typed: [field: keyof CollectionClosingAnswers, value: string];
    appraised: [appraisal: KnowledgeAppraisalAnswers];
    download: [];
}>();

/**
 * The two fields in order: how each is asked, and the height its text
 * deserves.
 */
const FIELDS: { key: keyof CollectionClosingAnswers; label: string; placeholder: string; rows: number }[] = [
    {
        key: 'questions',
        label: 'Le patient a-t-il des questions ou des remarques ?',
        placeholder: 'Noter ici les questions / remarques du patient…',
        rows: 2,
    },
    {
        key: 'conclusion',
        label: 'Conclusion',
        placeholder: "Conclusion de l'entretien de recueil…",
        rows: 4,
    },
];

/**
 * Whether the conclusion wears the badge: the AI's proposal, as long as it
 * reads as served — the first word the pharmacist changes makes the text
 * theirs, and the badge goes. Unlike the pre-filled answers of the interview
 * sheet, the source is named: the proposal is written by a LLM, and the
 * pharmacist should know it when they read it (ADR 0004).
 */
const prefilled = computed<boolean>(() => {
    if (props.frozen) {
        return props.view.conclusion_provenance === 'accepted';
    }

    return props.view.conclusion_provenance === 'proposed'
        && props.answers?.conclusion === props.view.conclusion;
});

const isConclusion = (field: keyof CollectionClosingAnswers): boolean => field === 'conclusion';

/**
 * What a field reads as: the local text while live, the one the server
 * froze once the block is completed.
 */
const valueOf = (field: keyof CollectionClosingAnswers): string => {
    return props.frozen ? props.view[field] ?? '' : props.answers?.[field] ?? '';
};

/**
 * Whether the recap can be downloaded: the HDS serves it once the block is
 * completed, so the button waits, disabled, for the validation.
 */
const recapServed = computed<boolean>(() => props.frozen && !!props.view.collection_recap);

/**
 * The conclusion grows with its text: the AI drafts several sentences at
 * once, and a fixed box would hand the pharmacist a text they must scroll to
 * reread — the one thing they are here to do.
 */
const conclusionField = ref<HTMLTextAreaElement>();

const {triggerResize} = useTextareaAutosize({element: conclusionField});

/**
 * Refit when the text lands from elsewhere — the draft arriving, or the
 * frozen record rendering whole. The measure waits for the render that
 * carries the new text: read one tick earlier, the box still holds the old
 * one and is sized for it.
 */
watch(
    () => valueOf('conclusion'),
    () => nextTick(triggerResize)
);

/**
 * Hold the conclusion's own element as the `v-for` lays the fields out: the
 * shadcn wrapper exposes the textarea as its root. The `v-for` hands a fresh
 * ref callback on every render, so the element is dropped and set again at
 * each one; only a real detach may clear it, or the refit would measure a
 * field that is momentarily nowhere.
 */
const bindConclusion = (field: keyof CollectionClosingAnswers, instance: unknown) => {
    if (!isConclusion(field)) {
        return;
    }

    const element = (instance as {$el?: HTMLTextAreaElement} | null)?.$el;

    if (element || !instance) {
        conclusionField.value = element;
    }
};

const typed = (field: keyof CollectionClosingAnswers, value: string | number) => {
    emit('typed', field, String(value));
};
</script>

<template>
  <div
    :class="CARD_SHEET"
    class="max-w-[640px] rounded-xl overflow-hidden"
  >
    <div class="flex items-center gap-3 px-4 py-3.5 bg-secondary-lightest text-secondary-darkest">
      <span class="w-9 h-9 shrink-0 rounded-full bg-white flex items-center justify-center">
        <Check class="w-5 h-5 text-secondary-darkest" :stroke-width="3" />
      </span>
      <span class="flex flex-col">
        <span class="text-base font-bold">Votre entretien est terminé !</span>
      </span>
    </div>

    <div class="flex flex-col gap-4 px-4 py-4">
      <div
        v-for="field in FIELDS"
        :key="field.key"
        class="flex flex-col gap-1.5"
      >
        <div class="flex flex-wrap items-center gap-2">
          <Label class="text-sm font-semibold">{{ field.label }}</Label>
          <MedicationCheckupAiBadge v-if="isConclusion(field.key) && prefilled" />
        </div>

        <div
          v-if="isConclusion(field.key) && drafting"
          class="flex items-center gap-1.5 text-xs text-muted-foreground"
        >
          <Sparkles class="w-3 h-3 shrink-0 text-primary" />
          <span>L'IA rédige une proposition de conclusion…</span>
          <LoaderCircle class="w-3 h-3 shrink-0 animate-spin" />
        </div>

        <Textarea
          v-if="!frozen || view[field.key]"
          :ref="instance => bindConclusion(field.key, instance)"
          :aria-label="field.label"
          :model-value="valueOf(field.key)"
          :placeholder="field.placeholder"
          :rows="field.rows"
          :disabled="frozen || (isConclusion(field.key) && drafting)"
          class="min-h-0 rounded-lg bg-white shadow-none resize-none focus-visible:ring-0 focus-visible:border-primary disabled:opacity-100 disabled:text-gray-dark"
          @update:model-value="typed(field.key, $event)"
        />
        <span v-else class="text-sm italic text-muted-foreground">Non renseigné</span>
      </div>

      <MedicationCheckupKnowledgeAppraisal
        :recorded="view"
        :frozen="frozen"
        :appraisal="appraisal"
        @appraised="emit('appraised', $event)"
      />
    </div>

    <MedicationCheckupBillingReminder
      :lines="view.billing"
      class="border-t border-b border-gray-light"
    />

    <div class="flex gap-2 items-center px-4 py-3">
      <Button
        variant="secondary"
        class="self-start inline-flex items-center gap-1.5 text-sm disabled:opacity-60 shrink-0"
        :disabled="!recapServed || downloading"
        @click="emit('download')"
      >
        <LoaderCircle v-if="downloading" class="animate-spin w-3.5 h-3.5" />
        <FileDown v-else class="w-3.5 h-3.5" />
        Télécharger le récapitulatif (PDF)
      </Button>
      <span class="text-sm text-muted-foreground">
        {{ recapServed ? 'Vous pourrez le retrouver plus tard' : 'Disponible après validation' }}
      </span>
    </div>
  </div>
</template>
